import React, { useState, useRef } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { EyeIcon, RocketIcon } from '../components/Icons';
import TemplatePreviewModal from '../components/TemplatePreviewModal';
import { templatePreviews } from '../lib/template-previews';
import PageWrapper from '../components/PageWrapper';

export interface Template {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  tags: string[];
  prompt: string;
}

// Template data
const templates: Template[] = [
  {
    id: 'aura-apparel',
    name: 'Aura - Modern Apparel',
    description: "A sleek, minimalist e-commerce template. This multi-page design features a clean product grid, an 'about' section, a contact form, and smooth animations.",
    imageUrl: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?q=80&w=2070&auto=format&fit=crop',
    tags: ['Fashion', 'Minimalist', 'Light Mode'],
    prompt: "Create a functional, multi-page e-commerce website for a modern apparel brand called 'Aura'. The design should be minimalist and sleek, with a light theme. It needs a home page with a large hero image, a featured products section, and an Instagram feed integration. Also, create a product catalog page with filters, a detailed product page, and a contact page. The header should have a shopping cart icon.",
  },
  {
    id: 'gadget-hub',
    name: 'Gadget Hub - Electronics Store',
    description: "A dark, futuristic theme for tech stores. This multi-page preview highlights product categories, specifications, an 'about' section, and a dynamic layout.",
    imageUrl: 'https://images.unsplash.com/photo-1526738549149-8e07eca6c147?q=80&w=1925&auto=format&fit=crop',
    tags: ['Electronics', 'Dark Mode', 'Futuristic'],
    prompt: "Generate a multi-page e-commerce store for 'Gadget Hub', an electronics retailer. The website must have a dark, futuristic theme with blue neon accents. The homepage needs a hero section with a video background, a 'New Arrivals' grid, and a 'Top Categories' section. Include a product listing page, a product detail page with a specs table, and an about us page. Ensure the site is fully responsive.",
  },
  {
    id: 'artisan-home',
    name: 'Artisan Home - Furniture & Decor',
    description: "A warm, inviting template for handcrafted goods. This multi-page design emphasizes storytelling with an 'our story' section, a full collection, and a cozy aesthetic.",
    imageUrl: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?q=80&w=2070&auto=format&fit=crop',
    tags: ['Furniture', 'Cozy', 'Brand-focused'],
    prompt: "Build a multi-page e-commerce website for 'Artisan Home', a store selling handcrafted furniture. The design should be warm, cozy, and inviting, using earthy tones. Key pages include: a home page showcasing the artisan process, a 'Shop by Room' page, individual product pages with multiple images and dimensions, and a blog for design tips. The navigation should be simple and intuitive.",
  },
  {
    id: 'glow-skincare',
    name: 'Glow - Skincare & Beauty',
    description: "A bright, elegant template for beauty products. This multi-page preview features sections for ingredients, customer testimonials, a full shop, and beautiful typography.",
    imageUrl: 'https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?q=80&w=1974&auto=format&fit=crop',
    tags: ['Beauty', 'Elegant', 'Health'],
    prompt: "Create an e-commerce website for a skincare brand called 'Glow'. The design must be bright, clean, and elegant, using a pastel color palette. The site should have a homepage with a hero banner, a section for 'Best Sellers', and customer testimonials. It also needs a shop page with product filtering by skin type, a detailed product page that highlights key ingredients, and a 'Our Story' page.",
  },
  {
    id: 'voyagers-post',
    name: "The Voyager's Post - Travel Blog",
    description: "A beautiful, content-focused blog template for writers and explorers. Features a prominent latest post, a grid of older articles, and a subscription form.",
    imageUrl: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=2070&auto=format&fit=crop',
    tags: ['Blog', 'Travel', 'Content-First'],
    prompt: "Create a multi-page travel blog website called 'The Voyager's Post'. The design should be clean and focus on high-quality images. The homepage needs a hero section for the latest featured post, followed by a grid of other blog posts. Each post should have its own page with a large header image and clean typography. Also include an 'About Me' page and a simple contact page. The footer should have a newsletter subscription form."
  },
  {
    id: 'devfolio-portfolio',
    name: "DevFolio - Developer Portfolio",
    description: "A sleek, dark-mode portfolio for developers. Showcase your projects, skills, and experience with this modern, single-page layout with smooth scrolling.",
    imageUrl: 'https://images.unsplash.com/photo-1550439062-609e1531270e?q=80&w=2070&auto=format&fit=crop',
    tags: ['Portfolio', 'Developer', 'Dark Mode'],
    prompt: "Build a modern, single-page developer portfolio with a dark theme and neon green accents. The sections should include: a hero section with a typing animation effect for my name 'Alex Doe' and title 'Full-Stack Developer', an 'About Me' section, a 'Skills' section with logos for technologies like React, Node.js, and Python, a 'Projects' section with cards for 3 projects (including image, title, description, and links), and a 'Contact' form. The navigation bar should smoothly scroll to each section."
  },
  {
    id: 'gilded-spoon-restaurant',
    name: "The Gilded Spoon - Restaurant",
    description: "An elegant and sophisticated website for a fine dining restaurant. Features an enticing hero section, a full menu display, an 'About' section, and a reservation form.",
    imageUrl: 'https://images.unsplash.com/photo-1552566626-52f8b828add9?q=80&w=2070&auto=format&fit=crop',
    tags: ['Restaurant', 'Elegant', 'Reservations'],
    prompt: "Create a sophisticated, multi-page website for a fine dining restaurant called 'The Gilded Spoon'. The design should be elegant with a black, white, and gold color scheme. The homepage needs a full-screen video background of a bustling kitchen, an 'About Us' summary, and testimonials. Create a separate 'Menu' page with categories for Appetizers, Main Courses, and Desserts. Also, create a 'Reservations' page with a form to book a table (fields: Name, Phone, Date, Time, Number of Guests). The footer should include the restaurant's address and opening hours."
  },
  {
    id: 'fit-forge-gym',
    name: 'FitForge - Gym Workouts',
    description: "A dark, motivational theme for a fitness website. Comes with two pre-defined workout plans for fat loss and muscle gain, an exercise guide, and a clean layout.",
    imageUrl: 'https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?q=80&w=1974&auto=format&fit=crop',
    tags: ['Fitness', 'Health', 'Dark Mode'],
    prompt: "Create a modern, dark-themed multi-page fitness website called 'FitForge'. The homepage should have a motivational hero section with a call-to-action button 'Start Your Journey'. Below that, have a section introducing two workout plans: 'Fat Loss' and 'Muscle Gain', with brief descriptions and links to their own pages. Create a separate page for the 'Fat Loss' plan that lists exercises like 'Running (30 mins)', 'Burpees (3 sets of 15)', and 'Jumping Jacks (3 sets of 50)'. Create another page for the 'Muscle Gain' plan that lists exercises like 'Bench Press (4 sets of 8-10)', 'Squats (4 sets of 8-10)', and 'Deadlifts (3 sets of 5)'. The website should have a simple navigation header and a footer."
  }
];


interface TemplateCardProps {
  template: Template;
  onUseTemplate: (prompt: string) => void;
  onPreview: (template: Template) => void;
  delay: number;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onUseTemplate, onPreview, delay }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const isVisible = useIntersectionObserver(cardRef, { threshold: 0.1 });
    
    return (
        <div
            ref={cardRef}
            className={`template-card group bg-[var(--panel-bg)] border border-[var(--border-color)] rounded-xl overflow-hidden shadow-lg transition-all duration-700 hover:shadow-2xl hover:-translate-y-2 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            <div className="overflow-hidden h-56">
                <img 
                  src={template.imageUrl} 
                  alt={`Preview of the ${template.name} template`} 
                  className="w-full h-full object-cover template-card-image"
                />
            </div>
            <div className="p-6">
                <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">{template.name}</h3>
                <p className="text-[var(--text-secondary)] text-sm mb-4 h-20">{template.description}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                    {template.tags.map(tag => (
                        <span key={tag} className="px-2 py-1 text-xs font-medium text-[var(--gradient-to)] bg-[var(--gradient-to)]/10 rounded-full">{tag}</span>
                    ))}
                </div>
                <div className="flex items-center space-x-4">
                    <button 
                      onClick={() => onUseTemplate(template.prompt)}
                      className="w-full flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] rounded-md hover:opacity-90 transition-opacity"
                    >
                      <RocketIcon />
                      <span className="ml-2">Use Template</span>
                    </button>
                    <button 
                        onClick={() => onPreview(template)}
                        className="w-full flex items-center justify-center px-4 py-2 text-sm font-semibold bg-white/10 text-[var(--text-primary)] rounded-md hover:bg-white/20 transition-colors"
                    >
                        <EyeIcon />
                        <span className="ml-2">Preview</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

interface TemplatesPageProps {
  startWorkspaceWithPrompt: (prompt: string) => void;
}


const TemplatesPage: React.FC<TemplatesPageProps> = ({ startWorkspaceWithPrompt }) => {
  const [previewingTemplate, setPreviewingTemplate] = useState<Template | null>(null);

  const handleUseTemplate = (prompt: string) => {
    setPreviewingTemplate(null); // Close modal if open
    startWorkspaceWithPrompt(prompt);
  }

  return (
    <PageWrapper>
      {previewingTemplate && (
          <TemplatePreviewModal 
              template={previewingTemplate}
              htmlContent={templatePreviews[previewingTemplate.id as keyof typeof templatePreviews] || ''}
              onClose={() => setPreviewingTemplate(null)}
              onUseTemplate={handleUseTemplate}
          />
      )}
      <div className="py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-extrabold text-[var(--text-primary)]">
              Codemine Templates
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-[var(--text-secondary)]">
              Launch your next project in minutes. Choose a template below and let Codey build the foundation for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
             {templates.map((template, index) => (
                <TemplateCard 
                    key={template.name}
                    template={template} 
                    onUseTemplate={startWorkspaceWithPrompt}
                    onPreview={setPreviewingTemplate}
                    delay={index * 100}
                />
             ))}
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default TemplatesPage;